import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UserContext } from "@/context/User/UserContext";
import { externalContributorCreate } from "@/services/external-contributors-functions";
import { RegisterExternalContributorParams } from "@/types/external-contributors.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
	email: z
		.string()
		.min(1, {
			message: "Email is required",
		})
		.email("Invalid email address"),
	first_name: z.string().min(1, {
		message: "First name is required",
	}),
	last_name: z.string().min(1, {
		message: "Last name is required",
	}),
	username: z
		.string()
		.min(1, {
			message: "Username is required",
		})
		.refine((val) => val === val.toLowerCase(), {
			message: "Username must be lowercase",
		})
		.refine((val) => !/\s/.test(val), {
			message: "Username must not contain spaces",
		}),
	password: z.string().min(6, {
		message: "Password must be at least 6 characters",
	}),
	phone: z.string().min(1, {
		message: "Phone number is required",
	}),
	payrollno: z.string().min(1, {
		message: "Payroll number is required",
	}),
});

const CreateExternalContributor = () => {
	const user = useContext(UserContext);
	const [showSuccess, setShowSuccess] = useState(false);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			first_name: "",
			last_name: "",
			username: "",
			password: "",
			phone: "",
			payrollno: "",
		},
	});

	const contibutorCreate = useMutation({
		mutationKey: ["job_create"],
		mutationFn: (data: RegisterExternalContributorParams) =>
			externalContributorCreate(user?.accessToken!, data),
		onSuccess: () => {
			toast.success("External Contributor created successfully", {
				description: "The External Contributor has been created successfully.",
			});
			form.reset();
			setShowSuccess(true);
			contibutorCreate.reset();
		},
		onError: (err) => {
			toast.error("Error creating External Contributor");
			console.log(err);
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		contibutorCreate.mutate(data);
	};

	const disableFields = () => {
		if (contibutorCreate.isLoading) return true;
		if (contibutorCreate.isSuccess) return true;
		return false;
	};

	// const scroll = () => {
	// 	if (errorElementRef.current && error) {
	// 		errorElementRef.current.scrollIntoView({
	// 			behavior: "smooth",
	// 			block: "end",
	// 		});
	// 	}
	// };

	// useEffect(() => {
	// 	scroll();
	// }, [error]);

	if (showSuccess) {
		return (
			<div className="flex justify-center items-center min-h-screen">
				<Card className="max-w-2xl mx-auto gap-2">
					<CardHeader className="flex justify-center mb-4">
						<CardTitle className="text-2xl">
							External Contributor Created Successfully
						</CardTitle>
					</CardHeader>
					<CardContent className="text-center mb-4">
						<p>Your External Contributor has been created successfully.</p>
					</CardContent>
					<CardFooter className="flex justify-center ">
						<Button onClick={() => setShowSuccess(false)}>Create Another</Button>
					</CardFooter>
				</Card>
			</div>
		);
	}

	return (
		<Card className="max-w-2xl mx-auto gap-2">
			<CardHeader>
				<CardTitle>Create External Contributor</CardTitle>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField
								name="first_name"
								render={({ field }) => (
									<FormItem className="mb-3">
										<FormLabel>First Name</FormLabel>
										<FormControl>
											<Input {...field} disabled={disableFields()} />
										</FormControl>
										<FormMessage className="font-light" />
									</FormItem>
								)}
							/>
							<FormField
								name="last_name"
								render={({ field }) => (
									<FormItem className="mb-3">
										<FormLabel>Last Name</FormLabel>
										<FormControl>
											<Input {...field} disabled={disableFields()} />
										</FormControl>
										<FormMessage className="font-light" />
									</FormItem>
								)}
							/>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField
								name="username"
								render={({ field }) => (
									<FormItem className="mb-3">
										<FormLabel>Username</FormLabel>
										<FormControl>
											<Input {...field} disabled={disableFields()} />
										</FormControl>
										<FormMessage className="font-light" />
									</FormItem>
								)}
							/>
							<FormField
								name="email"
								render={({ field }) => (
									<FormItem className="mb-3">
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input {...field} disabled={disableFields()} />
										</FormControl>
										<FormMessage className="font-light" />
									</FormItem>
								)}
							/>
						</div>
						<FormField
							name="password"
							render={({ field }) => (
								<FormItem className="mb-3">
									<FormLabel>Password</FormLabel>
									<FormControl>
										<Input {...field} disabled={disableFields()} />
									</FormControl>
									<FormMessage className="font-light" />
								</FormItem>
							)}
						/>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField
								name="phone"
								render={({ field }) => (
									<FormItem className="mb-3">
										<FormLabel>Phone Number</FormLabel>
										<FormControl>
											<Input {...field} disabled={disableFields()} />
										</FormControl>
										<FormMessage className="font-light" />
									</FormItem>
								)}
							/>
							<FormField
								name="payrollno"
								render={({ field }) => (
									<FormItem className="mb-3">
										<FormLabel>Payroll Number</FormLabel>
										<FormControl>
											<Input {...field} disabled={disableFields()} />
										</FormControl>
										<FormMessage className="font-light" />
									</FormItem>
								)}
							/>
						</div>
						<Button type="submit" className="mt-4 w-full" disabled={disableFields()}>
							{contibutorCreate.isLoading ? "Creating..." : "Submit Contributor"}
						</Button>
					</form>
				</Form>
			</CardContent>
		</Card>
	);
};

export default CreateExternalContributor;
